/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import React, {
  useEffect,
  useCallback,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import SettingsIcon from '@material-ui/icons/Settings'
import { ToggleButton, IconButton, Shrink as ShrinkIcon } from '@netbasequid/core'
import Highcharts from 'highcharts'
import { useDisplayTheme, useDashboardViewLayout } from 'hooks/useUiState'
import { setUiState, setDisplayTheme } from 'features/ui/uiSlice'
import ErrorCode from 'enums/ErrorCode'
import { hasLiveReportPermission, hasManagementDashboardPermission } from 'utils/permissionUtil'
import useRedrawId from 'hooks/useRedrawId'
import { heapTrack, HeapEvent } from 'utils/heapUtils'

import LiveReportConfigModal from 'pages/dashboard/main/LiveReportConfigModal'
import ScreenSpinner from 'components/share/ScreenSpinner'

import { getDashboardAlerts } from 'features/scheduledReport/asyncThunks'
import WidgetFactory from '../../../components/widget/WidgetFactory'
import WidgetContainer from '../../../components/dashboard/WidgetContainer'
import PlaceHolder from '../../../components/share/PlaceHolder'
import ErrorMessage from '../../../components/share/ErrorMessage'
// import OperationsList from './OperationsList'
import { getDashboard } from '../../../features/dashboard/asyncThunks'
import {
  ToggleLabel,
  StyledGridLayout,
  StyledWidgetContainer,
  StyledWidgetOperations,
  StyledOperationSection,
  StyledDashboardMain,
  StyledDashboardHeader,
  StyledDashboardTitleContainer,
  StyledDashboardTitle,
  StyledDashboardController,
  StyledGrid,
  StyledButton,
  StyledLabelText,
  StyledDashboardContent,
  GlobalStyleForDashboard,
  Copyright
} from './index.css'
import exportPdf from './pdfExport/pdfExporter'
import { useSetPresentationModeUrl } from '../view/hooks'
import ScheduledReportConfigModal from './ScheduledReportConfigModal'

const SettingButton = () => (
  <StyledButton onMouseDown={(e) => e.stopPropagation()}>
    <SettingsIcon fontSize="small" />
  </StyledButton>
)

const CloseButton = ({ onClick }) => (
  <StyledButton onClick={onClick} onMouseDown={(e) => e.stopPropagation()}>
    <CloseIcon fontSize="small" />
  </StyledButton>
)

CloseButton.propTypes = {
  onClick: PropTypes.func.isRequired
}

const PresentationHeader = ({ dashboardName, isExportMode }) => {
  const displayTheme = useDisplayTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const setPresentationModeUrl = useSetPresentationModeUrl()

  return (
    <StyledDashboardHeader>
      <StyledDashboardTitleContainer>
        <StyledDashboardTitle displayTheme={displayTheme}>{dashboardName}</StyledDashboardTitle>
      </StyledDashboardTitleContainer>
      {!isExportMode && (
        <StyledDashboardController>
          <ToggleLabel>
            <StyledLabelText displayTheme={displayTheme}>{t('darkMode')}</StyledLabelText>
            <ToggleButton
              mode={displayTheme}
              checked={displayTheme === 'dark'}
              onChange={(e) => {
                const selectedTheme = e.target.checked ? 'dark' : 'light'
                dispatch(setDisplayTheme(selectedTheme))
              }}
            />
          </ToggleLabel>
          <IconButton
            mode={displayTheme}
            color="secondary"
            variant="contained"
            onClick={() => {
              setPresentationModeUrl(false)
              heapTrack(HeapEvent.V4PresentationModeClick, { isPresentationMode: false })
            }}
            aria-label="Exit Fullscreen">
            <ShrinkIcon mode={displayTheme} />
          </IconButton>
        </StyledDashboardController>
      )}
    </StyledDashboardHeader>
  )
}
PresentationHeader.propTypes = {
  dashboardName: PropTypes.string,
  isExportMode: PropTypes.bool
}
PresentationHeader.defaultProps = {
  dashboardName: '',
  isExportMode: false
}

const DashboardMain = forwardRef((_, ref) => {
  const { dashboardId } = useParams()
  const { presentationMode, pinMode, exportMode } = useDashboardViewLayout()
  const { updateRedrawId } = useRedrawId()
  const dispatch = useDispatch()
  const dashboardRef = useRef(null)
  const gridRef = useRef(null)
  const { t } = useTranslation()
  const displayTheme = useDisplayTheme()
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          type: displayTheme
        }
      }),
    [displayTheme]
  )

  const {
    dashboardData,
    status,
    operatingStatus,
    editingFields,
    rowHeight,
    statusCode,
    pageConfig: pageConfigs,
    scheduledReportStatus
  } = useSelector(({ dashboard, ui, pageConfig, scheduledReport }) => {
    return {
      dashboardData: dashboard.dashboardData,
      status: dashboard.status,
      rowHeight: dashboard.rowHeight,
      editingFields: ui['dashboard/edit'],
      statusCode: dashboard.statusCode,
      pageConfig: pageConfig.config,
      scheduledReportStatus: scheduledReport.reportInfo.status
    }
  }, shallowEqual)

  const exportDashboardToPdf = useCallback(async () => {
    await exportPdf(dashboardData, dashboardRef, [])
  }, [dashboardData])

  useImperativeHandle(
    ref,
    () => {
      return {
        dashboardRef: dashboardRef.current,
        exportPDF: exportDashboardToPdf
      }
    },
    [exportDashboardToPdf]
  )

  const handleOnLayoutChange = useCallback(
    (layout) => {
      if (editingFields) {
        const updatedLayouts = editingFields.layout.map((item) => {
          const layoutProperties = Object.keys(item)
          return layout
            .filter((l) => item.i === l.i)
            .map((curr) =>
              layoutProperties.find((property) => !(item[property] === curr[property]))
            )
        })
        const isLayoutUpdated = updatedLayouts.find((l) => l[0] !== undefined)
        if (isLayoutUpdated) {
          dispatch(
            setUiState({
              'dashboard/edit': {
                ...editingFields,
                layout
              }
            })
          )
        }
      }
      for (let i = 0; i < Highcharts.charts.length; i += 1) {
        if (Highcharts.charts[i] !== undefined) {
          Highcharts.charts[i].reflow()
        }
      }
    },
    [dispatch, editingFields]
  )

  const handleRemoveItem = useCallback(
    (key) => {
      dispatch(
        setUiState({
          'dashboard/edit': {
            ...editingFields,
            layout: editingFields.layout.filter((item) => item.i !== key)
          }
        })
      )
    },
    [dispatch, editingFields]
  )

  const displayLiveReportButton = useMemo(() => {
    if (dashboardData && pageConfigs) {
      const isOwner = dashboardData.ownerInfo.id === pageConfigs?.user?.userProfileId
      return isOwner && hasLiveReportPermission(pageConfigs?.user?.userPermissions)
    }
    return false
  }, [dashboardData, pageConfigs])

  const displayScheduledReportButton = useMemo(() => {
    if (dashboardData && pageConfigs) {
      const isOwner = dashboardData.ownerInfo.id.toString() === pageConfigs?.user?.userProfileId
      return isOwner && hasManagementDashboardPermission(pageConfigs?.user?.userPermissions)
    }
    return false
  }, [dashboardData, pageConfigs])

  useEffect(() => {
    dispatch(getDashboard({ dashboardId, isExportMode: exportMode }))
    dispatch(getDashboardAlerts(dashboardId))
  }, [dispatch, dashboardId, exportMode])

  useEffect(() => {
    // * after presentation animation(0.3s) GridLayout need to re-render
    setTimeout(() => {
      if (gridRef.current) {
        gridRef.current.onWindowResize()
        updateRedrawId()
      }
      // * delay set to 500ms instead of 300ms to make it looks smoother
    }, [500])
  }, [presentationMode, pinMode, updateRedrawId])

  if (!['succeeded', 'failed'].includes(status)) return <ScreenSpinner fillContainer />

  return (
    <>
      <ThemeProvider theme={muiTheme}>
        {displayLiveReportButton && <LiveReportConfigModal dashboardId={dashboardId} />}
        <GlobalStyleForDashboard displayTheme={displayTheme} />
        <StyledDashboardMain
          displayTheme={displayTheme}
          ref={dashboardRef}
          isPresentationMode={presentationMode}>
          {(presentationMode || exportMode) && (
            <PresentationHeader
              dashboardName={dashboardData && dashboardData.name}
              isExportMode={exportMode}
            />
          )}
          {status === 'succeeded' ? (
            <>
              <StyledGridLayout
                ref={gridRef}
                cols={dashboardData.numOfColumn}
                rowHeight={rowHeight}
                margin={[16, 16]}
                containerPadding={[0, 0]}
                useCSSTransforms={false}
                isDraggable={!!editingFields}
                isResizable={!!editingFields}
                isBounded
                onLayoutChange={handleOnLayoutChange}>
                {editingFields
                  ? editingFields.layout.map((item, index) => (
                      <StyledGrid
                        displayTheme={displayTheme}
                        key={item.i}
                        data-grid={editingFields.layout[index]}
                        editingFields>
                        <StyledWidgetContainer>
                          <StyledWidgetOperations>
                            <input placeholder={dashboardData.dashboardName} />
                            <StyledOperationSection>
                              <SettingButton />
                              <CloseButton onClick={() => handleRemoveItem(item.i)} />
                            </StyledOperationSection>
                          </StyledWidgetOperations>
                          {/* {content[item.i]} */}
                        </StyledWidgetContainer>
                      </StyledGrid>
                    ))
                  : dashboardData.widgets.map((widget) => {
                      const widgetLayout = {
                        i: widget.widgetId.toString(),
                        x: widget.posX - 1,
                        y: widget.posY - 1,
                        w: widget.sizeX,
                        h: widget.sizeY
                      }
                      return (
                        <StyledGrid
                          key={widget.widgetId}
                          data-grid={widgetLayout}
                          displayTheme={displayTheme}>
                          <ErrorBoundary
                            FallbackComponent={(e) => <PlaceHolder>{e.error.message}</PlaceHolder>}>
                            <WidgetContainer
                              widgetLayout={widgetLayout}
                              widgetSetting={widget}
                              displayTheme={displayTheme}>
                              <WidgetFactory type={widget.type} widgetId={widget.widgetId} />
                            </WidgetContainer>
                          </ErrorBoundary>
                        </StyledGrid>
                      )
                    })}
              </StyledGridLayout>
              <Copyright mode={displayTheme}>
                {t('quidCopyright', { year: new Date().getFullYear() })}
              </Copyright>
            </>
          ) : (
            <StyledDashboardContent>
              <ErrorMessage>
                {statusCode === ErrorCode.PERMISSION_DENIED ? t('permissionDenied') : null}
              </ErrorMessage>
            </StyledDashboardContent>
          )}
        </StyledDashboardMain>
      </ThemeProvider>
    </>
  )
})

export default DashboardMain
